import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import PackingSession from '../PackingSession';
import { setTrackContext, flushTracking } from '../pp/track';
import { guidedText } from './guidedText';
import PacketaHandover from './PacketaHandover';
import HrxBoxes from './HrxBoxes';
import AnnouncementBar from './AnnouncementBar';
import IdentifyBox, { Identity, isBadgeCode } from './IdentifyBox';
import DeletedBox from './DeletedBox';
import { useReprintApproval } from './ReprintApproval';
import { useScanWedge } from '../scanWedge';

// The P&P station (laptop + fixed scanner) in a guided round (phase 4, 2026-10-17; docs/GUIDED-PP.md).
// Fernando (16.10): "Picking and Packing should have its own button … They click start and it happens automatically …
// They can start picking when the laptop directs them to. They can start packing when the tablet directs them to."
// Fernando (19.10): every step begins with "Identify yourself. Please enter your PIN or scan your badge." — the PIN or
// badge says who it is (no names to choose), and the WMS works out what comes next:
//   Start picking & packing → identify → the round, started by that person
//   Start packing           → identify → their round (here, or "go to your station"), or the only round waiting here
//   Packeta / Bring boxes / Deleted orders box → identify (with the right) → that task
// Nothing is chosen here that the server can choose, and nothing is remembered here: every poll asks the server.

interface Group { key: string; label: string; count: number; deadline: string | null; today: boolean; minutes_left: number | null }
interface Start { ready: number; priority: number; groups: Group[]; not_ready: { reason: string; text: string; count: number }[]; presets: number[]; max: number }
interface PutBack { order_id: string; order_number: string; excluded_reason: string | null; items: { location: string | null; product_code: string; qty: number }[];
  to_manager?: boolean; tab_label?: string }
interface RoundView {
  id: number; phase: string; person: string | null; name: string | null; station_no: number | null;
  packing_station_no: number | null; orders: number; packed: number; diverted: number;
  position: { phase: string; load: number; stop: number } | null; stops: number | null;
  notes_job: { id: number; status: string; error: string | null; printer: string } | null; notes_error?: string | null;
  still_to_pack?: number; label_not_scanned?: number; finish_ready?: boolean; label_pending?: string[];
  put_back?: PutBack[];
  order_rows?: { order_id: string; order_number: string; packed_at: string | null; excluded_at: string | null }[];
  // a manager's fix (2.11): no Finish, each order processed at its label scan
  fix?: boolean; fix_processed?: number; fix_left?: string[]; fix_notes?: string[];
}
interface StationState {
  station_no: number; label: string | null; person: string | null; name: string | null; perms: string[]; lang: string;
  rounds: RoundView[]; my_round: RoundView | null; start: Start | null; box: number;
  /** the Packeta handover's clock (3.11, ship_bulks.packeta_clock): soon = the button glows, due = the call */
  packeta: PacketaClock | null;
}
export interface PacketaClock {
  phase: 'none' | 'soon' | 'due' | 'started' | 'done'; pickup_at: string | null; remind_at: string | null; waiting: number;
  open: { id: number; code: string; ship_date: string | null; count: number; stale: boolean; started_by: string | null } | null;
  handed_over_today: { code: string; by: string; at: string } | null;
}
interface Ask { title: string; onSubmit: (id: Identity) => Promise<string | null> }
type Step2 = { kind: 'confirm'; station: number; roundId: number; id: Identity }
           | { kind: 'choose'; rounds: RoundView[]; id: Identity };

const hhmm = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

async function detail(r: Response) {
  const d = await r.json().catch(() => ({} as any));
  return typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`;
}

export default function StationApp() {
  const [st, setSt] = useState<StationState | null>(null);
  const [err, setErr] = useState('');
  const [pollErr, setPollErr] = useState('');    // the last state poll failed — cleared by the next good one (1.11)
  const [msg, setMsg] = useState('');
  const [count, setCount] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(false);
  const [finished, setFinished] = useState<{ rid: number; processed: number; labelIssue: number; notPacked: number } | null>(null);
  // Pack manually without the right: someone who has it identifies (Fernando: "invite a manager to verify")
  const [approval, setApproval] = useState<{ line: any; resolve: (ok: boolean) => void } | null>(null);
  const [mode, setMode] = useState<'round' | 'packeta' | 'hrx' | 'box'>('round');
  // the Packeta handover opened from a round (3.11): "Back to packing" returns to it — no sign-out
  const [packetaFromRound, setPacketaFromRound] = useState(false);
  // the packing screen is between two orders (its last label scanned) — only then the 10:00 call shows
  const [between, setBetween] = useState(true);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [step2, setStep2] = useState<Step2 | null>(null);
  const tx = guidedText(st?.lang);
  const navigate = useNavigate();
  // Printer jam: Tier 1 calls a manager, who enters their PIN or scans their badge (Fernando 19.10)
  const reprintApproval = useReprintApproval(tx.cancel);
  const reprintAsking = !!reprintApproval.box;

  // a failed poll shows its own banner and the next good poll takes it down again (Fernando 1.11: "No connection
  // to the WMS" stayed on the screen); action errors (err) are not touched by polling
  const load = useCallback(() => authFetch(`${API}/pp2/station/state`).then(async r => {
    if (!r.ok) { setPollErr(await detail(r)); return; }
    const d: StationState = await r.json();
    setSt(d);
    setPollErr('');
    setTrackContext({ actor: d.person, lang: d.lang, flowKey: 'guided', roundId: d.my_round?.id ?? null });
  }).catch(() => setPollErr('No connection to the WMS')), []);
  const packingNow = !!st?.my_round && st.my_round.phase === 'packing';
  useEffect(() => {
    load();
    const t = setInterval(load, packingNow ? 6000 : 2000);
    return () => clearInterval(t);
  }, [load, packingNow]);

  // a badge scanned with no question open: say what to press (a scan alone never starts or takes over anything);
  // the identify / approval boxes listen themselves while they are open
  useScanWedge(code => setErr(isBadgeCode(code) ? tx.pressFirst : tx.scanNotNow),
               { enabled: !(packingNow || mode !== 'round' || ask || approval || reprintAsking), skipFields: true });

  const signOut = async () => {
    await authFetch(`${API}/pp2/session/end`, { method: 'POST' }).catch(() => {});
    setFinished(null); setCount(''); setTick(false); setMode('round'); load();
  };

  const post = (path: string, body: any) => authFetch(`${API}/pp2/${path}`, { method: 'POST', body: JSON.stringify(body) });

  // ── the entries — each begins with "Identify yourself" ──
  const startRound = () => {
    const n = count;
    if (!n) return;
    setErr(''); setMsg('');
    setAsk({ title: tx.identifyStation, onSubmit: async id => {
      setBusy(true);
      try {
        const r = await post('rounds/start', { count: Number(n), ...id });
        if (!r.ok) { const m = await detail(r); load(); return m; }
        setAsk(null); setCount(''); setFinished(null); load();
        return null;
      } finally { setBusy(false); }
    } });
  };
  const pack = async (id: Identity, extra: any = {}): Promise<string | null> => {
    const r = await post('pack', { ...id, ...extra });
    if (!r.ok) return detail(r);
    const d = await r.json();
    setAsk(null);
    if (d.action === 'confirm') { setStep2({ kind: 'confirm', station: d.station, roundId: d.round_id, id }); return null; }
    if (d.action === 'choose') { setStep2({ kind: 'choose', rounds: d.rounds, id }); return null; }
    setStep2(null); setFinished(null); load();
    return null;
  };
  const startPacking = () => { setErr(''); setMsg(''); setAsk({ title: tx.identifyStation, onSubmit: id => pack(id) }); };
  // the person signed in here goes straight into today's Packeta handover (the 10:00 call, the header chip)
  const startPacketaNow = async () => {
    setErr('');
    const r = await authFetch(`${API}/bulks/packeta/start`, { method: 'POST' });
    if (!r.ok) { setErr(await detail(r)); return; }
    setPacketaFromRound(true); setMode('packeta'); load();
  };
  const startTask = (need: string, m: 'packeta' | 'hrx' | 'box') => {
    setErr(''); setMsg('');
    setAsk({ title: tx.identifyStation, onSubmit: async id => {
      const r = await post('station/sign-in', { ...id, need });
      if (!r.ok) return detail(r);
      setAsk(null); setPacketaFromRound(false); setMode(m); load();
      return null;
    } });
  };

  const reprint = async (rid: number) => {
    setErr('');
    const r = await reprintApproval.run(approver => post(`rounds/${rid}/notes/reprint`, { reason: 'printer jam', ...approver }));
    if (!r) return;                                  // the approval was cancelled
    if (!r.ok) { setErr(await detail(r)); return; }
    setMsg(tx.reprinted); load();
  };

  // a manager's fix (2.11): the order is processed at its label scan — the packing steps reach the server first
  // A finished fix goes back to the Orders page it was started from, with its result, and the manager is signed out
  // (Fernando 2.11: "finishes on the same orders page when we fix orders"; a station keeps no lingering session)
  const fixFinished = async (round: any, left = 0) => {
    const done = ((round?.order_rows || []) as any[]).filter(o => o.processed_at).map(o => o.order_number);
    const text = tx.fixDone(done.length) + (done.length ? `: ${done.join(', ')}` : '') + (left ? tx.fixLeftWhere(left) : '');
    await authFetch(`${API}/pp2/session/end`, { method: 'POST' }).catch(() => {});
    let back = '/orders/new';
    try { back = sessionStorage.getItem('wms_fix_from') || back; sessionStorage.removeItem('wms_fix_from'); } catch { /* the default */ }
    navigate(back, { state: { fixDone: text } });
  };
  const fixLabel = async (rid: number, orderId: string, code: string): Promise<{ ok: boolean; text: string } | null> => {
    try {
      await flushTracking();
      const r = await post(`fix/${rid}/process`, { order_id: orderId, code });
      const d = await r.json().catch(() => ({} as any));
      if (!r.ok) { load(); return { ok: false, text: typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}` }; }
      if (d.closed) { fixFinished(d.round); return null; }
      load();
      return { ok: !!d.processed, text: d.reason || '' };
    } catch {
      load();
      return { ok: false, text: 'No connection to the WMS' };
    }
  };
  const fixRelease = (rid: number, orderId: string, reason: string) => {
    post(`fix/${rid}/release`, { order_id: orderId, reason }).then(async r => {
      const d = r.ok ? await r.json().catch(() => ({} as any)) : {};
      if (d.closed) fixFinished(d.round); else load();
    }).catch(() => {});
  };
  const endFix = async (rid: number) => {
    if (!window.confirm(tx.endFixConfirm)) return;
    const r = await post(`fix/${rid}/end`, {});
    if (!r.ok) { setErr(await detail(r)); return; }
    const d = await r.json().catch(() => ({} as any));
    fixFinished(d.round, d.left || 0);
  };

  const finish = async (rid: number) => {
    setBusy(true); setErr('');
    flushTracking();
    await new Promise(res => setTimeout(res, 800));     // the last label scan reaches the server first
    try {
      const r = await post(`rounds/${rid}/finish`, { box_check: true });
      if (!r.ok) { setErr(await detail(r)); return; }
      const d = await r.json();
      setFinished({ rid, processed: d.result?.processed || 0, labelIssue: (d.result?.label_issue || []).length,
                    notPacked: (d.result?.not_packed || []).length });
      setTick(false); load();
    } catch { setErr('No connection to the WMS'); } finally { setBusy(false); }
  };

  if (!st) {
    return <div className="fixed inset-0 bg-[#0f1117] flex items-center justify-center text-gray-400">{pollErr || err || 'Loading…'}</div>;
  }
  const canManage = st.perms.includes('pp_manage');
  const manualGate = (line: any) => (st.perms.includes('pack_manually')
    ? Promise.resolve(true)
    : new Promise<boolean>(res => {
        // tell the managers someone waits (approvals.waiting — the box asks the PIN on the screen) (Fernando 20.10)
        post('approval-request', { order_id: line.order_id, line_id: line.line_id, product_code: line.product_code,
                                   name: line.name }).catch(() => {});
        setApproval({ line, resolve: res });
      }));
  const putBackDone = async (rid: number, oid: string) => {
    const r = await post(`rounds/${rid}/put-back`, { order_id: oid });
    if (!r.ok) { setErr(await detail(r)); return; }
    load();
  };
  const removeOrder = async (rid: number, oid: string, num: string) => {
    const note = window.prompt(`Take ${num} out of round ${rid} (back to New)? Why:`);
    if (!note) return;
    const r = await post(`rounds/${rid}/remove-order`, { order_id: oid, note });
    if (!r.ok) { setErr(await detail(r)); return; }
    load();
  };

  const askBox = ask && (
    <IdentifyBox title={ask.title} hint={tx.badgeOrPin} cancelLabel={tx.cancel} busyText={tx.identifyBusy} onSubmit={ask.onSubmit} onCancel={() => setAsk(null)} />
  );
  const approvalBox = approval && (
    <IdentifyBox title={tx.approveTitle} hint={tx.badgeOrPin} cancelLabel={tx.cancel} busyText={tx.identifyBusy}
      sub={<><div className="font-semibold">{approval.line.name || approval.line.product_code}</div><div>{tx.approveSub}</div></>}
      onSubmit={async id => {
        const r = await post('manual-approval', { ...id, order_id: approval.line.order_id, line_id: approval.line.line_id });
        if (!r.ok) return detail(r);
        approval.resolve(true); setApproval(null);
        return null;
      }}
      onCancel={() => { approval.resolve(false); setApproval(null); }} />
  );
  const step2Box = step2 && (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-6">
      <div className="max-w-xl w-full rounded-2xl bg-white p-6 space-y-4 text-center text-[#111111]">
        {step2.kind === 'confirm' ? (
          <>
            <div className="text-2xl font-black">{tx.otherStation(step2.station)}</div>
            <div className="flex justify-center gap-3">
              <button onClick={async () => { const e = await pack(step2.id, { here: true, round_id: step2.roundId }); if (e) { setErr(e); setStep2(null); } }}
                className="px-6 py-3 rounded-xl bg-[#111111] text-white text-lg font-bold">{tx.packHereBtn}</button>
              <button onClick={() => setStep2(null)} className="px-6 py-3 rounded-xl bg-white border border-[#9aa0a6] text-lg">{tx.cancel}</button>
            </div>
          </>
        ) : (
          <>
            <div className="text-2xl font-black">{tx.chooseRoundTitle}</div>
            <div className="space-y-2">
              {step2.rounds.map(r => (
                <button key={r.id} onClick={async () => { const e = await pack(step2.id, { round_id: r.id }); if (e) { setErr(e); setStep2(null); } }}
                  className="w-full px-4 py-3 rounded-xl border border-[#9aa0a6] text-lg hover:bg-[#f3f4f6]">
                  #{r.id} · {r.name || r.person} · {tx.phase[r.phase] || r.phase} · {r.orders}</button>
              ))}
            </div>
            <button onClick={() => setStep2(null)} className="px-6 py-3 rounded-xl bg-white border border-[#9aa0a6] text-lg">{tx.cancel}</button>
          </>
        )}
      </div>
    </div>
  );
  const putBackBox = (r: RoundView) => (r.put_back || []).length > 0 && (
    <div className="space-y-2">
      {(r.put_back || []).map(t => (
        <div key={t.order_id} className={`rounded-xl border-2 p-3 ${t.to_manager ? 'border-amber-500 bg-amber-900/20' : 'border-red-600 bg-red-900/20'}`}>
          <div className={`font-bold ${t.to_manager ? 'text-amber-200' : 'text-red-200'}`}>
            {t.to_manager ? tx.managerTitle(t.order_number, t.tab_label || 'Product exchange')
                          : tx.boxTitle(t.order_number, t.excluded_reason ? ` (${t.excluded_reason})` : '')}</div>
          <div className="text-white">{t.items.map(i => `${i.product_code} ×${i.qty}`).join('   |   ')}</div>
          <button onClick={() => putBackDone(r.id, t.order_id)}
            className={`mt-2 px-4 py-2 rounded-lg text-white font-semibold ${t.to_manager ? 'bg-amber-600 hover:bg-amber-500' : 'bg-red-700 hover:bg-red-600'}`}>
            {t.to_manager ? tx.managerDone : tx.boxDone}</button>
        </div>
      ))}
    </div>
  );
  const managerBox = (r: RoundView) => canManage && (
    <details className="text-sm text-gray-300">
      <summary className="cursor-pointer text-gray-400">Manager</summary>
      <div className="mt-2 space-y-1">
        {(r.order_rows || []).filter(o => !o.excluded_at).map(o => (
          <div key={o.order_id} className="flex items-center gap-2">
            <span className="w-28">{o.order_number}</span>
            <span className="text-gray-500 w-20">{o.packed_at ? 'packed' : ''}</span>
            <button onClick={() => removeOrder(r.id, o.order_id, o.order_number)} className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600">Remove</button>
          </div>
        ))}
      </div>
    </details>
  );
  const mine = st.my_round;

  // ── packing: the familiar scan flow, with the round on top ──
  const pk = st.packeta;
  const pkTime = pk?.pickup_at ? hhmm(pk.pickup_at) : '';
  const pkGlow = !!pk && (pk.phase === 'soon' || pk.phase === 'due');
  const pkTip = pk && pkGlow ? tx.packetaSoon(pkTime) : undefined;
  const pkDue = !!pk && pk.phase === 'due';
  const pkOther = pk?.phase === 'started' && pk.open?.started_by && pk.open.started_by !== st.person ? pk.open.started_by : null;

  if (st.person && mine && packingNow && !finished && mode === 'round') {
    const header = (
      <div className="rounded-2xl border border-gray-700 bg-[#1a1d27] p-4 space-y-3">
        <AnnouncementBar />
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-white font-black text-2xl">{mine.fix ? tx.fixTitle(mine.id) : `Round ${mine.id}`}</span>
          {/* orders = the live ones (diverted excluded already); the total shown is live + diverted, and the
              diverted are named, never subtracted ("19 of 18 packed", round 661 — Fernando 1.11) */}
          <span className="text-gray-300 text-lg">{mine.fix ? tx.fixProgress(mine.fix_processed || 0, mine.orders + mine.diverted)
            : tx.packed(mine.packed, mine.orders + mine.diverted)}{mine.diverted > 0 ? tx.diverted(mine.diverted) : ''}</span>
          <span className="text-gray-500">{st.name}</span>
          {/* Packeta (3.11): the open handover and what waits for it; it glows before the pickup and opens between orders */}
          {pk && !mine.fix && (pk.open || pk.waiting > 0 || pkGlow) && (
            <button title={pkTip || (pkOther ? tx.packetaOnIt(pkOther) : undefined)}
              onClick={() => { if (!between) { setErr(tx.packetaFinishOrder); return; } startPacketaNow(); }}
              className={`text-sm px-3 py-1.5 rounded-lg border font-semibold ${pkGlow ? 'bg-amber-500 text-black border-amber-300 animate-pulse' : 'bg-[#0f1117] text-gray-200 border-gray-600 hover:border-gray-400'}`}>
              {tx.packetaChip(pk.open?.count || 0, pk.waiting)}</button>
          )}
          {pkOther && <span className="text-gray-400 text-sm">{tx.packetaOnIt(pkOther)}</span>}
          {mine.fix && <button onClick={() => endFix(mine.id)} className="ml-auto text-amber-200 hover:text-white text-sm px-3 py-1.5 bg-amber-900/40 border border-amber-700 rounded-lg">{tx.endFix}</button>}
          <button onClick={signOut} className={`${mine.fix ? '' : 'ml-auto '}text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg`}>{tx.signOut}</button>
        </div>
        {mine.fix && !!mine.fix_left?.length && (
          <div className="text-gray-300">{tx.fixLeft(mine.fix_left.join(', '))}</div>
        )}
        {mine.fix && (mine.notes_error || mine.notes_job) && (
          <div className="text-sm">
            {mine.notes_error ? <span className="text-red-400">{tx.notesFailed(mine.notes_error)}</span>
              : mine.notes_job?.status === 'printed' ? <span className="text-green-400">{tx.notesPrinted} — {mine.notes_job.printer}</span>
              : mine.notes_job?.status === 'failed' ? <span className="text-red-400">{tx.notesFailed(mine.notes_job.error || '')}</span>
              : <span className="text-gray-300">{tx.notesPrinting} — {mine.notes_job?.printer}</span>}
          </div>
        )}
        {!!mine.label_pending?.length && !mine.finish_ready && (
          // a packed order whose parcel label was never scanned holds the Finish (Elsa, round 662: NO28551, 1.11)
          <div className="rounded-lg border border-amber-700 bg-amber-900/20 px-3 py-2 text-amber-200">
            {tx.labelPending(mine.label_pending.join(', '))}
          </div>
        )}
        {mine.finish_ready && (
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-800 pt-3">
            <label className="flex items-center gap-2 text-gray-200 text-base">
              <input type="checkbox" checked={tick} onChange={e => setTick(e.target.checked)} className="w-5 h-5" /> {tx.boxCheck}
            </label>
            <button disabled={!tick || busy} onClick={() => finish(mine.id)}
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-lg font-bold">
              {busy ? tx.finishing : tx.finish}</button>
          </div>
        )}
        {putBackBox(mine)}
        {managerBox(mine)}
        {err && <div className="text-red-400 text-sm">{err}</div>}
      </div>
    );
    return (
      <>
        <PackingSession onClose={signOut}
          guided={{ roundId: mine.id, header, onProgress: load, manualGate, onPhase: ph => setBetween(ph === 'scan_order'),
                    // a badge scanned while packing changes nothing by itself (Start packing does); at the Packeta
                    // pickup the next note waits until the handover is started (3.11)
                    intercept: code => {
                      if (isBadgeCode(code)) { setErr(tx.pressFirst); return true; }
                      if (pkDue && between && !mine.fix) { setErr(tx.packetaHeld); return true; }
                      return false; },
                    ...(mine.fix ? { onLabelScanned: (oid: string, code: string) => fixLabel(mine.id, oid, code),
                                     onReleased: (oid: string, why: string) => fixRelease(mine.id, oid, why),
                                     newNotes: mine.fix_notes || [] } : {}) }} />
        {approvalBox}
        {/* the Packeta call (3.11): at the pickup time, between two orders — "finish the order they are packing and then
            have a notification on the screen that it is time to handover Packeta and then a Start button" */}
        {pkDue && between && !mine.fix && (
          <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-6">
            <div className="w-full max-w-2xl rounded-3xl border-4 border-amber-400 bg-[#1a1d27] p-8 text-center space-y-5">
              <div className="text-4xl font-black text-amber-200">{tx.packetaDueTitle(pkTime)}</div>
              <div className="text-2xl text-gray-100">{tx.packetaDueSub((pk?.waiting || 0) + (pk?.open?.count || 0))}</div>
              <button onClick={startPacketaNow} className="px-12 py-6 rounded-2xl bg-amber-500 hover:bg-amber-400 text-black text-3xl font-black">{tx.packetaStart}</button>
              {err && <div className="text-red-400">{err}</div>}
            </div>
          </div>
        )}
      </>
    );
  }

  const roundsList = (
    <div className="space-y-2">
      <div className="text-gray-500 text-xs uppercase tracking-widest">{tx.roundsNow}</div>
      {st.rounds.length === 0 && <div className="text-gray-500 text-sm">{tx.noRounds}</div>}
      {st.rounds.map(r => (
        <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm text-gray-300 bg-[#1a1d27] border border-gray-800 rounded-lg px-3 py-2">
          <span className="text-white font-semibold">{r.fix ? tx.fixTitle(r.id) : `#${r.id}`}</span>
          <span>{r.name || r.person}</span>
          <span className="text-gray-400">· {tx.phase[r.phase] || r.phase}</span>
          {r.phase === 'picking' && r.position && r.stops ? <span className="text-gray-500">· trip {r.position.load + 1}, stop {r.position.stop + 1} ({r.stops} stops)</span> : null}
          {['picked', 'packing'].includes(r.phase) && <span className="text-gray-500">· {tx.packed(r.packed, r.orders + r.diverted)}{r.diverted > 0 ? tx.diverted(r.diverted) : ''}</span>}
          <span className="text-gray-600">· station {r.packing_station_no || r.station_no}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-[#0f1117] text-white flex flex-col overflow-y-auto">
      <div className="flex-none bg-[#1a1d27] border-b border-gray-800 px-6 py-3 flex items-center gap-4">
        <span className="font-bold">P&P · {st.label || `Station ${st.station_no}`}</span>
        {st.person && <span className="text-gray-300">{tx.hello(st.name || st.person)}</span>}
        {st.person && <button onClick={signOut} className="ml-auto text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">{tx.signOut}</button>}
        {/* the station opens on the Orders page; back there while nobody is identified (Fernando 19.10) */}
        {!st.person && <button onClick={() => navigate('/orders/new')} className="ml-auto text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">{tx.ordersPage}</button>}
      </div>
      {askBox}
      {step2Box}
      {approvalBox}
      {reprintApproval.box}

      <div className="flex-1 w-full max-w-5xl mx-auto px-6 py-8 space-y-8">
        <AnnouncementBar />
        {pollErr && <div className="text-amber-300 bg-amber-900/20 border border-amber-800/40 rounded-lg px-4 py-2">{pollErr}</div>}
        {err && <div className="text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2">{err}</div>}
        {msg && !err && <div className="text-green-300 bg-green-900/20 border border-green-800/40 rounded-lg px-4 py-2">{msg}</div>}

        {finished && (
          <div className="rounded-2xl border-2 border-emerald-600 bg-emerald-900/15 p-6 text-center space-y-3">
            <div className="text-3xl font-black text-emerald-200">{tx.finished(finished.processed)}</div>
            {(finished.labelIssue > 0 || finished.notPacked > 0) &&
              <div className="text-amber-200">{finished.labelIssue} → Label issue · {finished.notPacked} not packed</div>}
            <button onClick={() => setFinished(null)} className="px-6 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 text-lg">{tx.backToStart}</button>
          </div>
        )}

        {st.person && mode === 'packeta' && <PacketaHandover tx={tx} fromRound={packetaFromRound}
          onBack={() => { setMode('round'); if (packetaFromRound) { setPacketaFromRound(false); load(); } else signOut(); }} />}
        {st.person && mode === 'hrx' && <HrxBoxes onBack={() => { setMode('round'); signOut(); }} />}
        {st.person && mode === 'box' && <DeletedBox tx={tx} onBack={() => { setMode('round'); signOut(); }} />}

        {st.person && mine && mode === 'round' && !packingNow && (
          // Fernando 19.10: the instruction is what the screen is for — big, in the middle; the jam option is small,
          // at the bottom, out of the way
          <div className="rounded-2xl border border-gray-700 bg-[#1a1d27] px-8 py-10 min-h-[55vh] flex flex-col items-center justify-center text-center gap-6">
            {pkDue && (
              // waiting for the picker: the Packeta call here too, straight in (the person is signed in already)
              <button onClick={startPacketaNow} className="px-8 py-4 rounded-2xl bg-amber-500 hover:bg-amber-400 text-black text-2xl font-black">
                {tx.packetaDueTitle(pkTime)} — {tx.packetaStart}</button>
            )}
            {mine.phase === 'picked' ? (
              <>
                <div className="text-5xl font-black text-emerald-100 leading-tight">{tx.pickedReady(mine.id)}</div>
                <button onClick={startPacking} className="px-12 py-6 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-3xl font-black">{tx.startPacking}</button>
              </>
            ) : (
              <>
                <div className="text-5xl font-black leading-tight">{tx.goToTablet(mine.id)}</div>
                <div className="text-gray-200 text-2xl">{tx.goToTabletSub}</div>
                <div className="text-xl">
                  {mine.notes_error
                    ? <span className="text-red-400">{tx.notesFailed(mine.notes_error)}</span>
                    : mine.notes_job?.status === 'printed' ? <span className="text-green-400">{tx.notesPrinted}</span>
                    : mine.notes_job?.status === 'failed' ? <span className="text-red-400">{tx.notesFailed(mine.notes_job.error || '')}</span>
                    : <span className="text-gray-300">{tx.notesPrinting}</span>}
                </div>
                <div className="text-gray-500 text-base">{tx.phase[mine.phase] || mine.phase}
                  {mine.phase === 'picking' && mine.position && mine.stops ? ` · trip ${mine.position.load + 1}, stop ${mine.position.stop + 1} (${mine.stops} stops)` : ''}</div>
              </>
            )}
            <button onClick={() => reprint(mine.id)} className="mt-6 text-sm text-gray-500 hover:text-gray-300 underline underline-offset-4">
              {st.perms.includes('reprint') ? tx.printerJam : tx.printerJamManager}</button>
            {putBackBox(mine)}
            {managerBox(mine)}
          </div>
        )}

        {mode === 'round' && !(st.person && mine) && !finished && (
          <div className="space-y-6">
            {pkDue && (
              // the Packeta call on the start screen (3.11): Start asks for the badge or PIN, as every task here
              <div className="rounded-2xl border-2 border-amber-400 bg-amber-900/20 p-5 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[260px]">
                  <div className="text-2xl font-black text-amber-200">{tx.packetaDueTitle(pkTime)}</div>
                  <div className="text-gray-200">{tx.packetaDueSub((pk?.waiting || 0) + (pk?.open?.count || 0))}</div>
                </div>
                <button onClick={() => startTask('packeta_handover', 'packeta')} className="px-8 py-4 rounded-2xl bg-amber-500 hover:bg-amber-400 text-black text-2xl font-black">{tx.packetaStart}</button>
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button onClick={startPacking} className="px-6 py-3 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-lg font-bold">{tx.startPacking}</button>
              <button onClick={() => startTask('hrx_bulk', 'hrx')} className="px-5 py-3 rounded-xl bg-[#1a1d27] border border-gray-600 hover:border-gray-400 text-lg font-semibold">Bring boxes (HRX)</button>
              <button onClick={() => startTask('packeta_handover', 'packeta')} title={pkTip}
                className={`px-5 py-3 rounded-xl border text-lg font-semibold ${pkGlow ? 'bg-amber-500 text-black border-amber-300 animate-pulse' : 'bg-[#1a1d27] border-gray-600 hover:border-gray-400'}`}>
                {tx.packetaButton}{pk?.open ? ` · ${pk.open.count}` : ''}</button>
              <button onClick={() => startTask('replenish', 'box')} className="px-5 py-3 rounded-xl bg-[#1a1d27] border border-gray-600 hover:border-gray-400 text-lg font-semibold">{tx.boxButton(st.box || 0)}</button>
            </div>
            {st.start && (
              <>
                <div className="flex flex-wrap gap-4 items-stretch">
                  <div className="rounded-2xl bg-blue-900/30 border border-blue-700 px-8 py-6 min-w-[220px]">
                    <div className="text-5xl font-black">{st.start.ready}</div>
                    <div className="text-blue-200 text-lg">{tx.readyLabel}</div>
                    {st.start.priority > 0 && <div className="text-amber-300 text-sm mt-1">{tx.priorityFirst(st.start.priority)}</div>}
                  </div>
                  <div className="flex-1 flex flex-wrap gap-2 content-start">
                    {st.start.groups.map(g => (
                      <div key={g.key} className={`rounded-xl border px-4 py-3 min-w-[130px] ${g.count ? 'border-gray-600 bg-[#1a1d27]' : 'border-gray-800 bg-[#14161e] opacity-50'}`}>
                        <div className="text-2xl font-black">{g.count}</div>
                        <div className="text-gray-200 font-semibold">{g.label}</div>
                        <div className="text-gray-500 text-xs">{g.deadline ? tx.pickup(`${g.today ? '' : new Date(g.deadline).toLocaleDateString([], { weekday: 'short' }) + ' '}${hhmm(g.deadline)}`) : tx.noPickup}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-gray-300 text-lg mb-2">{tx.howMany}</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {st.start.presets.map(n => (
                      <button key={n} onClick={() => setCount(String(n))} disabled={st.start!.ready === 0}
                        className={`w-20 h-16 rounded-xl text-2xl font-black border ${count === String(n) ? 'bg-blue-600 border-blue-400' : 'bg-[#1a1d27] border-gray-700 hover:border-gray-500'} disabled:opacity-30`}>{n}</button>
                    ))}
                  </div>
                  <button onClick={startRound} disabled={busy || !count || st.start.ready === 0}
                    className="mt-5 px-12 py-5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-30 text-3xl font-black">
                    {busy ? tx.starting : tx.startPP}</button>
                </div>

                {st.perms.includes('pp_manage') && st.start.not_ready.length > 0 && (
                  <div className="text-sm text-gray-500">
                    <div className="uppercase tracking-widest text-xs mb-1">{tx.notReadyTitle}</div>
                    {st.start.not_ready.map(n => <div key={n.reason}>{n.count} · {n.text}</div>)}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {roundsList}
      </div>
    </div>
  );
}
